import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Play, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatCount, formatApiError } from "@/lib/api";
import { filterCss } from "@/lib/filters";
import { BlurBackdrop } from "@/components/shorts/BlurBackdrop";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function GridItem({ s, owner, onDelete, index }) {
  return (
    <div className="group relative aspect-[9/16] overflow-hidden rounded-xl bg-white/5 opacity-0 animate-[fadeUp_.45s_ease-out_forwards]" style={{ animationDelay: `${index * 40}ms` }} data-testid={`profile-post-${s.id}`}>
      <Link to={`/curtas/${s.id}`} className="absolute inset-0" data-testid={`profile-post-link-${s.id}`}>
        <BlurBackdrop src={s.poster_url} />
        <img src={s.poster_url} alt={s.caption} loading="lazy" style={{ filter: filterCss(s.edits?.filter) }} className="relative h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
        <span className="absolute left-2 top-2 text-white/90">{s.media_type === "photo" ? <ImageIcon size={15} /> : <Play size={15} fill="currentColor" />}</span>
        <div className="absolute bottom-2 left-2 flex items-center gap-3 text-xs font-bold" data-testid={`profile-post-stats-${s.id}`}>
          <span className="flex items-center gap-1"><Heart size={13} fill="currentColor" className="text-rose-500" /> {formatCount(s.likes)}</span>
          {owner && <span className="flex items-center gap-1"><MessageCircle size={13} /> {formatCount(s.comments_count)}</span>}
        </div>
      </Link>
      {owner && (
        <button onClick={() => onDelete(s)} aria-label="Excluir publicação" data-testid={`profile-post-delete-${s.id}`}
          className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-red-600">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

export function ProfileGrid({ items, owner, onRemoved }) {
  const [target, setTarget] = useState(null);
  const confirm = async () => {
    const s = target;
    setTarget(null);
    try {
      await api.delete(`/shorts/${s.id}`);
      onRemoved(s);
      toast.success("Publicação excluída");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!items.length) return <p className="py-16 text-center text-sm text-muted-foreground" data-testid="profile-empty">Ainda não há publicações.</p>;
  return (
    <>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-3" data-testid="profile-grid">
        {items.map((s, i) => <GridItem key={s.id} s={s} owner={owner} onDelete={setTarget} index={i} />)}
      </div>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="delete-short-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir publicação?</AlertDialogTitle>
            <AlertDialogDescription>A publicação, os comentários e a mídia na nuvem serão apagados permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-short-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} className="bg-red-600 hover:bg-red-500" data-testid="delete-short-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
